import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  CheckCircle as VerifiedIcon,
} from '@mui/icons-material';
import {
  getOnsiteChallans,
  deleteOnsiteChallan,
  bulkDeleteOnsiteChallans,
  exportOnsiteChallansCSV,
} from '../services/api';

const USER_BASE_URL =
  process.env.REACT_APP_USER_API_URL ||
  "https://backend.hunarmandpunjab.org.pk";

interface OnsiteChallanRecord {
  _id: string;
  challanId: string;
  psid: string;
  fee: number;
  status: string;
  generatedAt: string;
  paidAt?: string;
  txnId?: string;
  branchCode?: string;
  txnDate?: string;
  studentSnapshot?: {
    fullName?: string;
    phone?: string;
    email?: string;
    city?: string;
    courseApplied?: string;
  };
}

function OnsiteChallan() {
  const [challans, setChallans] = useState<OnsiteChallanRecord[]>([]);
  const [stats, setStats] = useState<{ total: number; paid: number; unpaid: number; recent: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [specificDate, setSpecificDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [submitDate, setSubmitDate] = useState('');
  
  // Single delete
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);
  const [deletingSingle, setDeletingSingle] = useState(false);

  // Bulk delete
  const [selectedChallanIds, setSelectedChallanIds] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);

  useEffect(() => {
    fetchChallans();
    setSelectedChallanIds([]);
  }, [
    page,
    rowsPerPage,
    search,
    statusFilter,
    startDate,
    endDate,
    specificDate,
    minAmount,
    maxAmount,
    submitDate,
  ]);

  const fetchChallans = async () => {
    try {
      setLoading(true);
      const data = await getOnsiteChallans(
        page + 1,
        rowsPerPage,
        search || undefined,
        statusFilter || undefined,
        specificDate ? specificDate : (startDate || undefined),
        specificDate ? specificDate : (endDate || undefined),
        minAmount || undefined,
        maxAmount || undefined,
        submitDate || undefined
      );
      setChallans(data.challans || []);
      setTotal(data.pagination?.total || 0);
      setStats(data.stats || null);
      setError('');
    } catch (err) {
      console.error('Error fetching onsite challans:', err);
      setError('Unable to load onsite challans.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setSpecificDate('');
    setMinAmount('');
    setMaxAmount('');
    setSubmitDate('');
    setPage(0);
  };

  const activeFilters = [
    search,
    statusFilter,
    startDate,
    endDate,
    specificDate,
    minAmount,
    maxAmount,
    submitDate,
  ].filter((value) => value && value.length > 0).length;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const openDeleteDialog = (id: string) => {
    setSelectedDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setSelectedDeleteId(null);
    setDeleteDialogOpen(false);
  };

  const confirmDelete = async () => {
    if (!selectedDeleteId) return;
    setDeletingSingle(true);
    try {
      await deleteOnsiteChallan(selectedDeleteId);
      setChallans((prev) => prev.filter((item) => item._id !== selectedDeleteId));
      setTotal((prev) => Math.max(prev - 1, 0));
      closeDeleteDialog();
    } catch (err) {
      console.error('Error deleting challan:', err);
      setError('Unable to delete challan.');
    } finally {
      setDeletingSingle(false);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelectedIds = challans.map((c) => c._id);
      setSelectedChallanIds(newSelectedIds);
    } else {
      setSelectedChallanIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    const selectedIndex = selectedChallanIds.indexOf(id);
    let newSelectedIds: string[] = [];

    if (selectedIndex === -1) {
      newSelectedIds = [...selectedChallanIds, id];
    } else {
      newSelectedIds = selectedChallanIds.filter((item) => item !== id);
    }

    setSelectedChallanIds(newSelectedIds);
  };

  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedChallanIds.length === 0) return;
    setDeletingBulk(true);
    try {
      await bulkDeleteOnsiteChallans(selectedChallanIds);
      alert(`${selectedChallanIds.length} onsite challans deleted successfully`);
      setChallans((prev) => prev.filter((c) => !selectedChallanIds.includes(c._id)));
      setTotal((prev) => Math.max(prev - selectedChallanIds.length, 0));
      setSelectedChallanIds([]);
      setBulkDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error bulk deleting challans:', err);
      alert('Unable to bulk delete challans.');
    } finally {
      setDeletingBulk(false);
    }
  };

  const handleDownloadCSV = () => {
    exportOnsiteChallansCSV({
      search,
      status: statusFilter,
      startDate: specificDate ? specificDate : startDate,
      endDate: specificDate ? specificDate : endDate,
      minAmount,
      maxAmount,
      submitDate,
    });
  };

  const handleCopyChallanLink = (challan: any) => {
    const fullLink = `${USER_BASE_URL}/api/onsite/challan/${challan.challanId}/pdf?view=true`;
    navigator.clipboard.writeText(fullLink)
      .then(() => {
        alert(`Challan link copied to clipboard:\n${fullLink}`);
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        alert("Failed to copy link to clipboard");
      });
  };

  const handleViewChallanPDF = (challan: any) => {
    const fullLink = `${USER_BASE_URL}/api/onsite/challan/${challan.challanId}/pdf?view=true`;
    window.open(fullLink, "_blank");
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Onsite Challan
      </Typography>

      {/* Stats Cards */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box p={1.5} sx={{ backgroundColor: '#e8f5e9', borderRadius: 1 }}>
              <ReceiptIcon color="success" />
            </Box>
            <Box>
              <Typography color="textSecondary" variant="subtitle2">Total Onsite Challans</Typography>
              <Typography variant="h5" fontWeight="bold">{stats?.total.toLocaleString() || '0'}</Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box p={1.5} sx={{ backgroundColor: '#e3f2fd', borderRadius: 1 }}>
              <VerifiedIcon color="primary" />
            </Box>
            <Box>
              <Typography color="textSecondary" variant="subtitle2">Paid Challans</Typography>
              <Typography variant="h5" fontWeight="bold">{stats?.paid.toLocaleString() || '0'}</Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box p={1.5} sx={{ backgroundColor: '#fafafa', borderRadius: 1 }}>
              <CancelIcon color="action" />
            </Box>
            <Box>
              <Typography color="textSecondary" variant="subtitle2">Unpaid Challans</Typography>
              <Typography variant="h5" fontWeight="bold">{stats?.unpaid.toLocaleString() || '0'}</Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box p={1.5} sx={{ backgroundColor: '#f3e5f5', borderRadius: 1 }}>
              <MoneyIcon color="secondary" />
            </Box>
            <Box>
              <Typography color="textSecondary" variant="subtitle2">Recent (7 Days)</Typography>
              <Typography variant="h5" fontWeight="bold">{stats?.recent.toLocaleString() || '0'}</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            {activeFilters > 0 && (
              <Chip label={`${activeFilters} active`} size="small" sx={{ ml: 1 }} />
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCSV}
              disabled={loading || total === 0}
            >
              Export CSV
            </Button>
            {activeFilters > 0 && (
              <IconButton onClick={clearFilters} title="Clear Filters">
                <CancelIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            label="Search"
            placeholder="Challan ID, PSID, Name, Phone, Email"
            value={search}
            onChange={handleSearchChange}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value as string);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Filter by Date"
            type="date"
            value={specificDate}
            onChange={(e) => {
              setSpecificDate(e.target.value);
              setStartDate('');
              setEndDate('');
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setSpecificDate('');
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            disabled={!!specificDate}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setSpecificDate('');
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            disabled={!!specificDate}
          />
          <TextField
            label="Min Fee"
            type="number"
            value={minAmount}
            onChange={(e) => {
              setMinAmount(e.target.value);
              setPage(0);
            }}
            placeholder="0"
            sx={{ minWidth: 120 }}
          />
          <TextField
            label="Max Fee"
            type="number"
            value={maxAmount}
            onChange={(e) => {
              setMaxAmount(e.target.value);
              setPage(0);
            }}
            placeholder="10000"
            sx={{ minWidth: 120 }}
          />
          <TextField
            label="Payment Date"
            type="date"
            value={submitDate}
            onChange={(e) => {
              setSubmitDate(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
        </Box>
      </Paper>

      {selectedChallanIds.length > 0 && (
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: '#ffebee', borderRadius: 1 }}>
          <Typography color="error" variant="subtitle1" fontWeight="600">
            {selectedChallanIds.length} challans selected
          </Typography>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
          >
            Delete Selected
          </Button>
        </Box>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedChallanIds.length > 0 && selectedChallanIds.length < challans.length}
                    checked={challans.length > 0 && selectedChallanIds.length === challans.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Challan ID</TableCell>
                <TableCell>PSID</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Fee</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Generated</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : challans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No onsite challans found.
                  </TableCell>
                </TableRow>
              ) : (
                challans.map((challan) => {
                  const isItemSelected = selectedChallanIds.indexOf(challan._id) !== -1;
                  return (
                    <TableRow key={challan._id} hover selected={isItemSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() => handleSelectOne(challan._id)}
                        />
                      </TableCell>
                      <TableCell>{challan.challanId}</TableCell>
                      <TableCell>{challan.psid}</TableCell>
                      <TableCell>{challan.studentSnapshot?.fullName || '-'}</TableCell>
                      <TableCell>{challan.studentSnapshot?.phone || '-'}</TableCell>
                      <TableCell>{challan.studentSnapshot?.courseApplied || '-'}</TableCell>
                      <TableCell>{challan.studentSnapshot?.city || '-'}</TableCell>
                      <TableCell>Rs. {challan.fee?.toLocaleString() || 0}</TableCell>
                      <TableCell>
                        <Chip
                          icon={challan.status === 'paid' ? <VerifiedIcon /> : <CancelIcon />}
                          label={challan.status === 'paid' ? 'Paid' : 'Unpaid'}
                          color={challan.status === 'paid' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {challan.generatedAt
                          ? new Date(challan.generatedAt).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <Tooltip title="Copy Challan Link">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleCopyChallanLink(challan)}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View PDF">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewChallanPDF(challan)}
                            >
                              <PdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Challan">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog(challan._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {/* Delete Single Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Onsite Challan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this onsite challan? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deletingSingle}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deletingSingle}>
            {deletingSingle ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Bulk Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => !deletingBulk && setBulkDeleteDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Bulk Delete Onsite Challans
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedChallanIds.length}</strong> selected onsite challans? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)} disabled={deletingBulk}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmBulkDelete} disabled={deletingBulk}>
            {deletingBulk ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OnsiteChallan;
